/**
 * Frost Tiers — adaptive liquid-glass resolver.
 *
 * One `.frost` primitive, three tiers, resolved ONCE from cheap device signals
 * and written to `data-frost-tier` on <html>. Surfaces read `var(--fg-*)`
 * (defined in src/styles/_tailwind.css) and never branch on the tier.
 *
 * Tier 1 (Solid) is the default when no attribute is set == today's flat,
 * near-opaque look, so the worst case is exactly the baseline the app ships.
 * Tier 2 (Frost) adds a moderate blur; Tier 3 (Liquid) the full glass.
 */
export type FrostTier = 1 | 2 | 3;

export interface FrostSignals {
  /** logical CPU cores (navigator.hardwareConcurrency) */
  cpu: number | null;
  /** approximate device RAM in GB (navigator.deviceMemory) */
  mem: number | null;
  reducedTransparency: boolean;
  reducedMotion: boolean;
  supportsBackdrop: boolean;
  saveData: boolean;
  /** touch/coarse pointer → treated as a mobile GPU, capped at Tier 2 */
  coarsePointer: boolean;
}

const mediaMatches = (q: string): boolean => {
  try {
    return window.matchMedia(q).matches;
  } catch {
    return false;
  }
};

export function readFrostSignals(): FrostSignals {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };
  return {
    cpu: nav.hardwareConcurrency ?? null,
    mem: nav.deviceMemory ?? null,
    reducedTransparency: mediaMatches('(prefers-reduced-transparency: reduce)'),
    reducedMotion: mediaMatches('(prefers-reduced-motion: reduce)'),
    supportsBackdrop: !!(
      window.CSS &&
      CSS.supports &&
      (CSS.supports('backdrop-filter', 'blur(1px)') || CSS.supports('-webkit-backdrop-filter', 'blur(1px)'))
    ),
    saveData: !!nav.connection?.saveData,
    coarsePointer: mediaMatches('(pointer: coarse)')
  };
}

/**
 * Pick the tier for this device. Conservative by design: any hard blocker
 * (no backdrop-filter support, reduced-transparency, data-saver) forces Tier 1;
 * otherwise weak CPU/RAM caps the ceiling. `why` explains the decision (used by
 * the DEV playground readout).
 */
export function resolveFrostTier(signals: FrostSignals = readFrostSignals()): {
  tier: FrostTier;
  signals: FrostSignals;
  why: string[];
} {
  if (!signals.supportsBackdrop) return { tier: 1, signals, why: ['sin backdrop-filter'] };
  if (signals.reducedTransparency) return { tier: 1, signals, why: ['reduced-transparency'] };
  if (signals.saveData) return { tier: 1, signals, why: ['saveData'] };

  const why: string[] = [];
  // Touch devices are treated as mobile GPUs: capped at Tier 2 (moderate blur,
  // no grain) so the feTurbulence grain — which mis-renders as static/banding on
  // low-end Mali/Samsung — stays desktop-only by capability, not by viewport.
  let tier: FrostTier = signals.coarsePointer ? 2 : 3;
  if (signals.coarsePointer) why.push('coarse-pointer → max Tier 2');
  if (signals.cpu != null && signals.cpu < 4) {
    tier = 1;
    why.push('cpu<4');
  } else if (signals.cpu != null && signals.cpu < 8) {
    tier = 2;
    why.push('cpu<8');
  }
  if (signals.mem != null && signals.mem <= 4) {
    tier = 1;
    why.push('mem≤4GB');
  } else if (signals.mem != null && signals.mem <= 6 && tier > 2) {
    tier = 2;
    why.push('mem≤6GB');
  }
  if (signals.reducedMotion) why.push('reduced-motion → grain off');
  if (why.length === 0) why.push('device sobrado → Liquid');
  return { tier, signals, why };
}

const FROST_TIER_ATTR = 'data-frost-tier';

export function applyFrostTier(tier: FrostTier): void {
  document.documentElement.setAttribute(FROST_TIER_ATTR, String(tier));
}

/** Remove the attribute → every surface falls back to Tier 1 (flat). */
export function clearFrostTier(): void {
  document.documentElement.removeAttribute(FROST_TIER_ATTR);
}
